/** The hub's scrolling list: sections, rows of every kind, selection, empty and loading states. */
import { Fragment, useEffect, useMemo, useRef, type ReactNode } from "react";
import type { DurableConversation } from "@/api/conversations";
import { Button, Icons, PocketRow } from "@/components/ui";
import type { NotificationActions } from "@/components/panels/notifications/notification-item";
import {
  NotificationItemRow,
  NotificationSection,
} from "@/components/panels/notifications/notification-list";
import { useT } from "@/i18n";
import { panelsSearchMessages } from "@/i18n/messages/panels-search";
import { ResultRow } from "./result-row";
import { HubLoading, SearchResultsStatus } from "./search-results-status";
import type { HubItem, HubSection, HubView } from "./hub-types";
import type { HubChats } from "./use-hub-chats";

export interface HubBodyProps {
  view: HubView;
  query: string;
  selectedIndex: number;
  onActivate: (item: HubItem) => void;
  onLookInAllProjects: () => void;
  notificationActions: NotificationActions;
  chats: Pick<
    HubChats,
    | "menu"
    | "loadMore"
    | "loadingMore"
    | "loadMoreArchived"
    | "archivedLoadingMore"
  >;
  onRenameChat: (chat: DurableConversation) => void;
}

export function HubBody({
  view,
  query,
  selectedIndex,
  onActivate,
  onLookInAllProjects,
  notificationActions,
  chats,
  onRenameChat,
}: HubBodyProps) {
  const t = useT(panelsSearchMessages);
  const scrollRef = useRef<HTMLDivElement>(null);
  const indexByKey = useMemo(() => {
    const indexes = new Map<string, number>();
    for (const item of view.sections.flatMap((section) => section.items))
      indexes.set(item.key, indexes.size);
    return indexes;
  }, [view]);

  useEffect(() => {
    scrollRef.current
      ?.querySelector<HTMLElement>(`[data-hub-index="${selectedIndex}"]`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [selectedIndex]);

  const renderItem = (item: HubItem, selected: boolean): ReactNode => {
    switch (item.type) {
      case "result":
        return (
          <ResultRow
            result={item.result}
            query={query}
            selected={selected}
            onOpen={() => onActivate(item)}
            menu={
              item.result.kind === "chat" && item.result.chat
                ? chats.menu(item.result.chat, onRenameChat)
                : null
            }
          />
        );
      case "notification":
      case "approval":
        return (
          <NotificationItemRow
            item={item}
            query={query}
            selected={selected}
            onOpen={() => onActivate(item)}
            actions={notificationActions}
          />
        );
      case "show-all":
        return (
          <PocketRow
            testId={`hub-row-show-all-${item.group}`}
            leading={
              <Icons.ChevronRight
                className="h-4 w-4 text-muted"
                aria-hidden="true"
              />
            }
            title={
              <span className="text-muted">
                {t("showAll", { count: item.count })}
              </span>
            }
            selected={selected}
            onSelect={() => onActivate(item)}
          />
        );
      case "archived": {
        const Chevron = item.expanded ? Icons.ChevronDown : Icons.ChevronRight;
        return (
          <PocketRow
            testId="hub-row-archived-chats"
            leading={
              <Chevron className="h-4 w-4 text-muted" aria-hidden="true" />
            }
            title={t("archivedChats")}
            trailing={
              <span className="text-appkit-caption font-medium text-muted">
                {item.count}
              </span>
            }
            selected={selected}
            onSelect={() => onActivate(item)}
          />
        );
      }
    }
  };

  const renderSection = (section: HubSection) => (
    <NotificationSection
      key={section.id}
      section={section}
      actions={notificationActions}
      testId={`hub-section-${section.id}`}
    >
      {section.items.map((item) => {
        const index = indexByKey.get(item.key) ?? -1;
        return (
          <div key={item.key} data-hub-index={index}>
            {renderItem(item, index === selectedIndex)}
          </div>
        );
      })}
    </NotificationSection>
  );

  const loadMoreChats = view.loadMoreChats ? (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      className="mx-auto my-2 flex"
      disabled={chats.loadingMore}
      onClick={() => void chats.loadMore()}
      data-testid="hub-chats-load-more"
    >
      {chats.loadingMore ? t("loading") : t("loadMore")}
    </Button>
  ) : null;
  const hasArchived = view.sections.some(
    (section) => section.id === "archived",
  );

  return (
    <div
      ref={scrollRef}
      className="scrollbar-none -mx-1 min-h-0 flex-1 overflow-y-auto px-1"
      data-testid="search-hub-results"
    >
      {view.loading ? <HubLoading /> : null}
      {view.empty ? (
        <SearchResultsStatus
          empty={view.empty}
          onLookInAllProjects={onLookInAllProjects}
        />
      ) : null}
      {view.sections.map((section) => (
        <Fragment key={section.id}>
          {section.id === "archived" ? loadMoreChats : null}
          {renderSection(section)}
        </Fragment>
      ))}
      {hasArchived ? null : loadMoreChats}
      {view.loadMoreArchived ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="mx-auto my-2 flex"
          disabled={chats.archivedLoadingMore}
          onClick={() => void chats.loadMoreArchived()}
          data-testid="hub-archived-load-more"
        >
          {chats.archivedLoadingMore ? t("loading") : t("loadMore")}
        </Button>
      ) : null}
    </div>
  );
}
